import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Upload, AlertCircle, RefreshCw } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { selfAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';

const RELATIONS = ['spouse', 'son', 'daughter', 'father', 'mother', 'father_in_law', 'mother_in_law'];

const REJECTION_LABELS = {
  invalid_documents: 'Invalid Documents',
  incorrect_information: 'Incorrect Information',
  relationship_proof_missing: 'Relationship Proof Missing',
  other: 'Other',
};

const emptyMember = {
  member_name: '', relation: 'spouse', date_of_birth: '', gender: 'female',
  mobile_number: '', aadhaar: '', documentIds: [],
};

export default function EmployeeFamilyRequest() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resubmitId = searchParams.get('resubmit');
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parentRequest, setParentRequest] = useState(null);
  const [member, setMember] = useState({ ...emptyMember });
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [declaration, setDeclaration] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    Promise.all([
      selfAPI.policy(),
      resubmitId ? selfAPI.getFamilyRequest(resubmitId).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ])
      .then(([policyRes, parentRes]) => {
        setPolicy(policyRes.data);
        const parent = parentRes.data;
        if (parent?.status === 'rejected') {
          setParentRequest(parent);
          const addItem = parent.items?.find((i) => i.action === 'add') || parent.items?.[0];
          if (addItem) {
            setMember({
              member_name: addItem.member_name || '',
              relation: addItem.relation || 'spouse',
              date_of_birth: addItem.date_of_birth?.slice(0, 10) || '',
              gender: addItem.gender || 'female',
              mobile_number: addItem.mobile_number || '',
              aadhaar: '',
              documentIds: [],
            });
          }
        }
      })
      .finally(() => setLoading(false));
  }, [resubmitId]);

  useEffect(() => {
    if (!member.member_name || !member.date_of_birth || !policy) {
      setPreview(null);
      setPreviewError(null);
      return undefined;
    }
    if (!parentRequest && policy.enrollmentWindow?.isLocked) {
      setPreview(null);
      setPreviewError(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await selfAPI.previewFamilyPremium({
          changes: [{ action: 'add', ...member }],
        });
        if (data.error) {
          setPreview(null);
          setPreviewError(data.error);
        } else {
          setPreview(data);
          setPreviewError(null);
        }
      } catch (err) {
        setPreview(null);
        setPreviewError(err.response?.data?.error || 'Unable to calculate premium');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [member, policy, parentRequest]);

  const handleUpload = async () => {
    if (!docFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('document', docFile);
    formData.append('document_type', 'relationship_proof');
    try {
      const { data } = await selfAPI.uploadFamilyDocument(formData);
      setMember((m) => ({ ...m, documentIds: [...m.documentIds, data.id] }));
      setDocFile(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!declaration) return;
    setSubmitting(true);
    setSubmitError(null);
    const idempotencyKey = `fam-${Date.now()}-${member.member_name}`;
    const payload = {
      declarationAccepted: true,
      changes: [{ action: 'add', ...member }],
    };
    try {
      const { data } = parentRequest
        ? await selfAPI.resubmitFamilyRequest(parentRequest.id, payload, idempotencyKey)
        : await selfAPI.submitFamilyRequest(payload, idempotencyKey);
      navigate('/', { state: { message: data.message } });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!policy) return <p className="text-slate-500">Policy not found.</p>;

  const locked = policy.enrollmentWindow?.isLocked && !parentRequest;
  const hasPending = policy.pendingRequest;
  const canSubmit = !hasPending && (!locked || parentRequest);

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to="/" className="text-primary-600 text-sm">← Back to My Coverage</Link>
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          {parentRequest ? <RefreshCw className="w-7 h-7 text-primary-600" /> : <Users className="w-7 h-7 text-primary-600" />}
          {parentRequest ? 'Resubmit Family Request' : 'Add Family Member'}
        </h1>
        <p className="text-slate-500 mt-1">
          {parentRequest
            ? 'Correct the details below and resubmit for Super Admin approval'
            : 'Submit for Super Admin approval — premium updates after approval'}
        </p>
      </div>

      {parentRequest && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm space-y-2">
          <p className="font-medium text-red-900">Previous request {parentRequest.request_number} was rejected</p>
          <p><strong>Reason:</strong> {REJECTION_LABELS[parentRequest.rejection_reason_code] || parentRequest.rejection_reason_code}</p>
          <p><strong>Remarks:</strong> {parentRequest.rejection_remarks}</p>
        </div>
      )}

      {!canSubmit && locked && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3 text-sm text-amber-900">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <div>
            <p>{policy.enrollmentWindow.message}</p>
            {policy.resubmitableRequest && (
              <Link to={`/my-family?resubmit=${policy.resubmitableRequest.id}`} className="text-primary-700 font-medium underline mt-1 inline-block">
                Resubmit rejected request {policy.resubmitableRequest.request_number}
              </Link>
            )}
          </div>
        </div>
      )}

      {hasPending && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          Pending request <strong>{policy.pendingRequest.request_number}</strong> — wait for approval or{' '}
          <Link to={`/my-family/requests/${policy.pendingRequest.id}`} className="text-primary-700 font-medium underline">
            view / cancel
          </Link>
          {' '}before submitting another.
        </div>
      )}

      {canSubmit && (
        <>
          <div className="card grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name *</label>
              <input className="input-field" required value={member.member_name}
                onChange={(e) => setMember({ ...member, member_name: e.target.value })} />
            </div>
            <div>
              <label className="label">Relation *</label>
              <select className="input-field" value={member.relation}
                onChange={(e) => setMember({ ...member, relation: e.target.value })}>
                {RELATIONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Date of Birth *</label>
              <input type="date" className="input-field" required value={member.date_of_birth}
                onChange={(e) => setMember({ ...member, date_of_birth: e.target.value })} />
            </div>
            <div>
              <label className="label">Gender *</label>
              <select className="input-field" value={member.gender}
                onChange={(e) => setMember({ ...member, gender: e.target.value })}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="label">Mobile (optional)</label>
              <input className="input-field" value={member.mobile_number}
                onChange={(e) => setMember({ ...member, mobile_number: e.target.value })} />
            </div>
            <div>
              <label className="label">Aadhaar (optional)</label>
              <input className="input-field" maxLength={12} value={member.aadhaar}
                onChange={(e) => setMember({ ...member, aadhaar: e.target.value })} />
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Upload className="w-4 h-4" />Supporting Documents</h3>
            <div className="flex flex-wrap gap-3 items-end">
              <input type="file" className="input-field text-sm" accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setDocFile(e.target.files?.[0])} />
              <button type="button" className="btn-secondary text-sm" disabled={!docFile || uploading} onClick={handleUpload}>
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
            {member.documentIds.length > 0 && (
              <p className="text-sm text-green-700 mt-2">{member.documentIds.length} document(s) uploaded</p>
            )}
          </div>

          {(preview || previewError) && (
            <div className={`card text-sm ${previewError ? 'border-red-200 bg-red-50' : 'border-primary-100 bg-primary-50'}`}>
              <h3 className="font-semibold mb-3">Premium Impact Preview</h3>
              {previewError && <p className="text-red-700">{previewError}</p>}
              {preview && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-slate-500 block">Current Premium</span>
                    <strong>{formatCurrency(preview.currentPremium)}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">New Premium</span>
                    <strong>{formatCurrency(preview.proposedPremium)}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Difference</span>
                    <strong className={preview.difference > 0 ? 'text-red-700' : 'text-green-700'}>
                      {preview.difference > 0 ? '+' : ''}{formatCurrency(preview.difference)}
                    </strong>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="btn-primary"
            disabled={!preview || previewError || member.documentIds.length === 0}
            onClick={() => setShowConfirm(true)}
          >
            Review & Submit Request
          </button>
          {member.documentIds.length === 0 && preview && !previewError && (
            <p className="text-sm text-amber-700">Upload at least one supporting document before submitting.</p>
          )}
        </>
      )}

      {showConfirm && preview && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-bold">{parentRequest ? 'Confirm Resubmission' : 'Confirm Submission'}</h3>
            <div className="text-sm space-y-2">
              <p><strong>Member:</strong> {member.member_name} ({member.relation.replace(/_/g, ' ')})</p>
              <p><strong>Current Premium:</strong> {formatCurrency(preview.currentPremium)}</p>
              <p><strong>New Premium:</strong> {formatCurrency(preview.proposedPremium)}</p>
              <p><strong>Difference:</strong> {formatCurrency(preview.difference)}</p>
            </div>
            <label className="flex gap-2 text-sm items-start">
              <input type="checkbox" checked={declaration} onChange={(e) => setDeclaration(e.target.checked)} className="mt-1" />
              <span>I confirm that all information provided is accurate and I understand that policy premium may change after approval.</span>
            </label>
            {submitError && <p className="text-red-600 text-sm">{submitError}</p>}
            <div className="flex gap-3">
              <button type="button" className="btn-primary flex-1" disabled={!declaration || submitting} onClick={handleSubmit}>
                {submitting ? 'Submitting…' : parentRequest ? 'Confirm & Resubmit' : 'Confirm & Submit'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
